import { pbQuery } from "@sergio9929/pb-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreVerticalIcon, PlusIcon } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { deleteCollectionItemModal } from "@/components/admin/DeleteModal";
import { EmptyFallback, ErrorFallback, LoadingFallback } from "@/components/fallbacks";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDate } from "@/lib/dates";
import { pb } from "@/lib/pocketbase";
import type { SafeOmit } from "@/lib/utils";
import { formulariesresponsesQueries } from "@/modules/formularies/formularies-responses/queries";
import { fillMedicalFormModal } from "@/modules/patients/medical-history/fill-medical-form-modal";
import { selectMedicalFormModal } from "@/modules/patients/medical-history/select-medical-form-modal";
import { Collections, type FormulariesResponse, type FormulariesResponsesResponse } from "@/types/pocketbase.ts";

export const Route = createFileRoute("/dashboard/patients/$patientId/medical-history")({
	component: RouteComponent,
});

type ExpandFormulary = {
	formulary: FormulariesResponse;
};

//TODO: Add translations

type Search = SafeOmit<FormulariesResponsesResponse, "formulary"> & ExpandFormulary;

function RouteComponent() {
	const { t } = useTranslation();
	const { patientId } = Route.useParams();

	const onCreate = useCallback(() => {
		selectMedicalFormModal.open({
			onSuccess: (formularyId) => {
				selectMedicalFormModal.close();
				fillMedicalFormModal.open({
					formularyId,
					patientId,
					onSuccess: () => {
						fillMedicalFormModal.close();
					},
				});
			},
		});
	}, [patientId]);

	return (
		<div className="space-y-4">
			<div className="flex justify-end">
				<Button onClick={onCreate}>
					<PlusIcon className="size-4" />
					{t(($) => $.common.actions.create)}
				</Button>
			</div>
			<FormulariesResponseList />
		</div>
	);
}

const FormulariesResponseList = () => {
	const { patientId } = Route.useParams();
	const queryClient = useQueryClient();

	const query = pbQuery<Search>()
		.expand("formulary")
		.equal("patient", patientId)
		.build((raw, params) => pb.filter(raw, params));

	const formularyResponses = useQuery(
		formulariesresponsesQueries.fullList<ExpandFormulary>({
			options: query,
		})
	);

	const onEdit = useCallback(
		({ id, formularyId }: { id: string; formularyId: string }) => {
			fillMedicalFormModal.open({
				formularyResponseId: id,
				formularyId,
				patientId,
				onSuccess: () => {
					fillMedicalFormModal.close();
				},
			});
		},
		[patientId]
	);

	const onDelete = useCallback(
		({ id }: { id: string }) => {
			deleteCollectionItemModal.open({
				items: [{ collection: Collections.FormulariesResponses, id }],
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: formulariesresponsesQueries.fullLists(),
					});
				},
			});
		},
		[queryClient]
	);

	if (formularyResponses.isLoading) {
		return <LoadingFallback className="mx-auto mt-18 max-w-sm" title="Cargando formularios del paciente " />;
	}

	if (formularyResponses.isError) {
		return <ErrorFallback className="mx-auto mt-18 max-w-sm" title="Error cargando formularios del paciente" />;
	}

	if (formularyResponses?.data?.length === 0) {
		return (
			<EmptyFallback className="mx-auto mt-18 max-w-sm" title="No se encontraron formularios para este paciente.">
				<p>No se encontraron documentos asociados a este paciente.</p>
			</EmptyFallback>
		);
	}

	return (
		<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{formularyResponses.data?.map((formularies) => {
				return (
					<MedicalHistoryCard formularies={formularies} key={formularies.id} onDelete={onDelete} onEdit={onEdit} />
				);
			})}
		</div>
	);
};

const MedicalHistoryCard = ({
	formularies,
	onEdit,
	onDelete,
}: {
	formularies: FormulariesResponsesResponse<unknown, ExpandFormulary>;
	onEdit: ({ id, formularyId }: { id: string; formularyId: string }) => void;
	onDelete: ({ id }: { id: string }) => void;
}) => {
	const { t } = useTranslation();
	const lastModified = formatDate(formularies.updated, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

	return (
		<div className="relative">
			<Card className="shrink-0">
				<CardHeader>
					<CardTitle className="line-clamp-2 h-8">{formularies.expand.formulary.name}</CardTitle>
					<CardDescription className="line-clamp-2 h-10">Last modified: {lastModified}</CardDescription>
				</CardHeader>
			</Card>

			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button className="absolute top-2 right-2" variant="ghost">
						<MoreVerticalIcon className="size-4" />
					</Button>
				</DropdownMenuTrigger>

				<DropdownMenuContent align="end">
					<DropdownMenuItem
						onClick={() => onEdit({ id: formularies.id, formularyId: formularies.expand.formulary.id })}
					>
						{t(($) => $.common.actions.edit)}
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => onDelete({ id: formularies.id })} variant="destructive">
						{t(($) => $.common.actions.delete)}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};
